import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { key: 'dashboard', label: '📊 Dashboard', shortLabel: '📊', mobileLabel: 'Home' },
  { key: 'moderation', label: '🛡️ Moderation', shortLabel: '🛡️', mobileLabel: 'Moderate' },
  { key: 'communities', label: '💬 Spaces', shortLabel: '💬', mobileLabel: 'Spaces' },
  { key: 'employees', label: '👥 Employees', shortLabel: '👥', mobileLabel: 'Team' },
  { key: 'settings', label: '⚙️ Settings', shortLabel: '⚙️', mobileLabel: 'Settings' },
  { key: 'insights', label: '📈 Insights', shortLabel: '📈', mobileLabel: 'Insights' },
  { key: 'billing', label: '💳 Billing', shortLabel: '💳', mobileLabel: 'Billing' },
];

function OrgAdminDashboard() {
  const navigate = useNavigate();
  const params = useParams();
  const { orgSlug } = useOrg();
  const { logout } = useAuth();
  const slug = params.slug || orgSlug;
  const { orgName, orgLogoUrl, orgBrandColor } = useOrg();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [orgId, setOrgId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tab-specific state
  const [dashboardData, setDashboardData] = useState(null);
  const [employeesData, setEmployeesData] = useState(null);
  const [communitiesData, setCommunitiesData] = useState(null);
  const [settingsData, setSettingsData] = useState(null);
  const [moderationData, setModerationData] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchOrgId();
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchTabData(activeTab);
    }
  }, [orgId, activeTab]);

  const fetchOrgId = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get('/api/org/onboard/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const memberships = res.data.memberships || [];
      if (memberships.length > 0) {
        setOrgId(memberships[0].org_id);
      } else {
        setError('No organization found. Please join an organization first.');
      }
    } catch (err) {
      setError('Failed to load organization info.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    setTabLoading(true);

    try {
      switch (tab) {
        case 'dashboard': {
          const res = await axios.get(`/api/org-admin/dashboard?orgId=${orgId}`, { headers });
          setDashboardData(res.data);
          break;
        }
        case 'employees': {
          const res = await axios.get(`/api/org-admin/employees?orgId=${orgId}`, { headers });
          setEmployeesData(res.data);
          break;
        }
        case 'communities': {
          const res = await axios.get(`/api/org-admin/communities?orgId=${orgId}`, { headers });
          setCommunitiesData(res.data);
          break;
        }
        case 'moderation': {
          // Fetch pending category requests and reported posts
          const [catReqRes, auditRes] = await Promise.all([
            axios.get(`/api/org-admin/audit-log?orgId=${orgId}`, { headers }).catch(() => ({ data: [] })),
            Promise.resolve({ data: [] }) // reported posts would come from a separate endpoint
          ]);
          setModerationData({ auditLog: catReqRes.data });
          break;
        }
        case 'settings': {
          const res = await axios.get(`/api/orgs/${orgId}`, { headers });
          setSettingsData(res.data);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      console.error(`Failed to fetch ${tab} data:`, err);
    } finally {
      setTabLoading(false);
    }
  };

  const handleToggleCategory = async (categoryId, currentEnabled) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `/api/org-admin/communities/${categoryId}?orgId=${orgId}`,
        { enabled: !currentEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTabData('communities');
    } catch (err) {
      console.error('Failed to toggle category:', err);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorCard}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate(slug ? `/org/${slug}/dashboard` : '/dashboard')} style={styles.backBtn}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="admin-dashboard-container">
      {/* Mobile Top Header */}
      <header style={styles.mobileHeader} className="admin-mobile-header">
        <div style={styles.mobileHeaderLeft}>
          {orgLogoUrl ? (
            <img src={orgLogoUrl} alt={orgName} style={{ height: '28px', borderRadius: '4px' }}
              onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <span style={{ fontSize: '20px' }}>🏢</span>
          )}
          <span style={styles.mobileHeaderTitle}>{orgName || 'Admin'}</span>
        </div>
        <button
          style={styles.hamburgerBtn}
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          ☰
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div style={styles.mobileOverlay} onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Slide-out Mobile Menu */}
      <div style={{
        ...styles.mobileDrawer,
        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
      }}>
        <div style={styles.mobileDrawerHeader}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Menu</h2>
          <button
            style={styles.mobileDrawerClose}
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
        <nav style={styles.mobileDrawerNav}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setMobileMenuOpen(false);
                if (tab.key === 'insights') {
                  navigate(slug ? `/org/${slug}/admin/insights` : '/org/admin/insights');
                } else {
                  setActiveTab(tab.key);
                }
              }}
              style={{
                ...styles.mobileDrawerItem,
                backgroundColor: activeTab === tab.key ? '#f5f3ff' : 'transparent',
                color: activeTab === tab.key ? '#7C3AED' : '#374151',
                fontWeight: activeTab === tab.key ? '600' : '500',
              }}
            >
              {tab.label}
            </button>
          ))}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />
          <button
            onClick={() => { setMobileMenuOpen(false); navigate(slug ? `/org/${slug}/dashboard` : '/dashboard'); }}
            style={styles.mobileDrawerItem}
          >
            👤 Employee View
          </button>
          <button
            onClick={() => { setMobileMenuOpen(false); logout(); navigate('/login'); }}
            style={{ ...styles.mobileDrawerItem, color: '#dc2626' }}
          >
            🚪 Logout
          </button>
        </nav>
      </div>

      {/* Desktop Sidebar - hidden on mobile via CSS class */}
      <aside style={styles.sidebar} className="admin-sidebar-desktop">
        <div style={styles.sidebarHeader}>
          {orgLogoUrl ? (
            <img src={orgLogoUrl} alt={orgName} style={{ height: '36px', borderRadius: '6px', marginBottom: '8px', display: 'block' }}
              onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>🏢</div>
          )}
          <h2 style={styles.sidebarTitle}>{orgName || 'Org Admin'}</h2>
          <div style={{ fontSize: '11px', color: '#c4b5fd', marginTop: '2px' }}>Admin Dashboard</div>
        </div>
        <nav style={styles.nav}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.key === 'insights') {
                  navigate(slug ? `/org/${slug}/admin/insights` : '/org/admin/insights');
                } else {
                  setActiveTab(tab.key);
                }
              }}
              style={{
                ...styles.navItem,
                backgroundColor: activeTab === tab.key ? '#7C3AED' : 'transparent',
                color: activeTab === tab.key ? '#fff' : '#e0d4f7',
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div style={styles.sidebarFooter}>
          <button onClick={() => setActiveTab('dashboard')} style={styles.backLink}>
            ← Admin Home
          </button>
          <button onClick={() => navigate(slug ? `/org/${slug}/dashboard` : '/dashboard')} style={{ ...styles.backLink, marginTop: '8px', opacity: 0.7 }}>
            👤 Employee View
          </button>
          <button onClick={() => { logout(); navigate('/login'); }} style={{ ...styles.backLink, marginTop: '12px', color: '#fca5a5' }}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={styles.main} className="admin-main-content">
        {tabLoading ? (
          <div style={styles.tabLoading}>Loading...</div>
        ) : (
          <>
            {activeTab === 'dashboard' && <DashboardTab data={dashboardData} />}
            {activeTab === 'settings' && <SettingsTab data={settingsData} orgId={orgId} />}
            {activeTab === 'employees' && <EmployeesTab data={employeesData} />}
            {activeTab === 'communities' && (
              <CommunitiesTab data={communitiesData} onToggle={handleToggleCategory} />
            )}
            {activeTab === 'moderation' && <ModerationTab orgId={orgId} />}
            {activeTab === 'billing' && <BillingTab />}
          </>
        )}
      </main>

      {/* Mobile Bottom Tab Bar */}
      <nav style={styles.mobileBottomBar} className="admin-bottom-bar">
        {TABS.slice(0, 5).map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              if (tab.key === 'insights') {
                navigate(slug ? `/org/${slug}/admin/insights` : '/org/admin/insights');
              } else {
                setActiveTab(tab.key);
              }
            }}
            style={{
              ...styles.bottomBarItem,
              color: activeTab === tab.key ? '#7C3AED' : '#6b7280',
            }}
            aria-label={tab.mobileLabel}
          >
            <span style={styles.bottomBarIcon}>{tab.shortLabel}</span>
            <span style={styles.bottomBarLabel}>{tab.mobileLabel}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ---- Tab Components ---- */

function DashboardTab({ data }) {
  if (!data) return <p style={styles.emptyText}>No dashboard data available.</p>;

  // Get org slug from URL
  const pathParts = window.location.pathname.split('/');
  const slugIndex = pathParts.indexOf('org') + 1;
  const orgSlug = (slugIndex > 0 && pathParts[slugIndex] !== 'admin') ? pathParts[slugIndex] : null;
  const inviteLink = orgSlug
    ? `${window.location.origin}/org/${orgSlug}/onboard`
    : `${window.location.origin}/org/onboard`;

  return (
    <div>
      <h1 style={styles.pageTitle}>Dashboard Overview</h1>
      <div style={styles.metricGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.employee_count ?? data.employeeCount ?? '—'}</div>
          <div style={styles.metricLabel}>Employees</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.active_channels ?? data.activeChannels ?? '—'}</div>
          <div style={styles.metricLabel}>Active Spaces</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.plan_display_name ?? data.planName ?? 'Free'}</div>
          <div style={styles.metricLabel}>Current Plan</div>
        </div>
      </div>

      {/* Invite Employees Section */}
      <div style={{ marginTop: '20px', backgroundColor: '#f5f3ff', borderRadius: '12px', padding: '16px', border: '1px solid #ddd6fe' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#4c1d95' }}>📨 Invite Employees</h3>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#555' }}>
          Share this link with your employees so they can join your organization's anonymous communities:
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            readOnly
            value={inviteLink}
            style={{ flex: '1 1 200px', minWidth: 0, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', backgroundColor: '#fff', boxSizing: 'border-box' }}
            onClick={(e) => e.target.select()}
          />
          <button
            onClick={() => { navigator.clipboard.writeText(inviteLink); alert('Link copied!'); }}
            style={{ padding: '10px 16px', backgroundColor: '#7C3AED', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Copy Link
          </button>
        </div>
        <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#888' }}>
          Employees will need their work email (matching your org domain) to join.
        </p>
      </div>

      {/* Getting Started Guide */}
      <div style={{ marginTop: '20px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #e5e7eb' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1f2937' }}>🚀 Getting Started</h3>
        <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#555', lineHeight: '2' }}>
          <li><strong>Spaces tab</strong> — Enable identity categories your employees can join</li>
          <li><strong>Share the invite link</strong> — Send it via Slack, email, or internal comms</li>
          <li><strong>Insights tab</strong> — View aggregated, anonymous engagement metrics (once employees join)</li>
        </ol>
      </div>
    </div>
  );
}

function SettingsTab({ data, orgId }) {
  const org = data?.org || data;
  const [form, setForm] = useState({
    name: org?.name || '',
    logo_url: org?.logo_url || '',
    brand_color: org?.brand_color || '#7C3AED',
    industry: org?.industry || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  if (!data) return <p style={styles.emptyText}>No settings data available.</p>;

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/orgs/${orgId}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: '14px',
    border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box',
    maxWidth: '100%',
  };

  return (
    <div>
      <h1 style={styles.pageTitle}>Organization Settings</h1>
      <div style={styles.settingsCard}>

        <div style={styles.settingsRow}>
          <span style={styles.settingsLabel}>Organization Name</span>
          <input name="name" value={form.name} onChange={handleChange} style={inputStyle} />
        </div>

        <div style={styles.settingsRow}>
          <span style={styles.settingsLabel}>Logo URL</span>
          <div style={{ width: '100%' }}>
            <input name="logo_url" value={form.logo_url} onChange={handleChange} placeholder="https://yourcompany.com/logo.png" style={inputStyle} />
            {form.logo_url && (
              <img src={form.logo_url} alt="Logo preview" style={{ marginTop: '8px', height: '40px', borderRadius: '4px' }} onError={(e) => e.target.style.display = 'none'} />
            )}
          </div>
        </div>

        <div style={styles.settingsRow}>
          <span style={styles.settingsLabel}>Brand Color</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <input type="color" name="brand_color" value={form.brand_color} onChange={handleChange}
              style={{ width: '48px', height: '36px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', padding: '2px' }} />
            <input name="brand_color" value={form.brand_color} onChange={handleChange}
              placeholder="#7C3AED" style={{ ...inputStyle, maxWidth: '140px', flex: 'none' }} />
          </div>
        </div>

        <div style={styles.settingsRow}>
          <span style={styles.settingsLabel}>Industry</span>
          <select name="industry" value={form.industry} onChange={handleChange} style={{ ...inputStyle, maxWidth: '100%' }}>
            {['', 'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing', 'Retail', 'Government', 'Non-profit', 'Other']
              .map(i => <option key={i} value={i}>{i || 'Select...'}</option>)}
          </select>
        </div>

        <div style={styles.settingsRow}>
          <span style={styles.settingsLabel}>Domains</span>
          <span style={styles.settingsValue}>
            {org.domains && org.domains.length > 0
              ? org.domains.map((d) => (
                  <span key={d.domain_id || d.domain} style={styles.domainTag}>
                    {d.domain}{d.dns_verified ? ' ✅' : ' ⏳'}
                  </span>
                ))
              : '—'}
          </span>
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '12px' }}>{error}</p>}

        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 24px', backgroundColor: '#7C3AED', color: '#fff',
            border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
          }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saved && <span style={{ color: '#16a34a', fontSize: '14px', fontWeight: '500' }}>✅ Saved</span>}
        </div>
      </div>
    </div>
  );
}

function EmployeesTab({ data }) {
  if (!data) return <p style={styles.emptyText}>No employee data available.</p>;

  return (
    <div>
      <h1 style={styles.pageTitle}>Employees</h1>
      <div style={styles.metricGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.total_count ?? data.totalCount ?? data.total ?? '—'}</div>
          <div style={styles.metricLabel}>Total Employees</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.active_count ?? data.activeCount ?? data.active ?? '—'}</div>
          <div style={styles.metricLabel}>Active</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{data.inactive_count ?? data.inactiveCount ?? data.inactive ?? '—'}</div>
          <div style={styles.metricLabel}>Inactive</div>
        </div>
      </div>
    </div>
  );
}

function CommunitiesTab({ data, onToggle }) {
  const categories = data?.categories || data || [];

  if (!Array.isArray(categories) || categories.length === 0) {
    return (
      <div>
        <h1 style={styles.pageTitle}>Spaces</h1>
        <p style={styles.emptyText}>No identity categories configured yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={styles.pageTitle}>Spaces</h1>
      <div style={styles.communityList}>
        {categories.map((cat) => {
          const id = cat.category_id || cat.org_category_id || cat.id;
          const enabled = cat.is_enabled !== undefined ? cat.is_enabled : true;
          const name = cat.display_name || cat.category_name || cat.name || 'Unknown';
          return (
            <div key={id} style={styles.communityRow}>
              <div>
                <div style={styles.communityName}>{cat.icon ? `${cat.icon} ` : ''}{name}</div>
                {cat.channel_name && (
                  <div style={styles.communityChannel}>#{cat.channel_name}</div>
                )}
              </div>
              <button
                onClick={() => onToggle(id, enabled)}
                style={{
                  ...styles.toggleBtn,
                  backgroundColor: enabled ? '#7C3AED' : '#d1d5db',
                }}
                aria-label={`${enabled ? 'Disable' : 'Enable'} ${name}`}
              >
                {enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModerationTab({ orgId }) {
  const [categoryRequests, setCategoryRequests] = useState([]);
  const [reportedPosts, setReportedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchModerationData();
  }, [orgId]);

  const fetchModerationData = async () => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      // Fetch pending category requests for this org
      const reqRes = await axios.get(`/api/org-admin/audit-log?orgId=${orgId}`, { headers }).catch(() => ({ data: [] }));
      // For now, show audit log as moderation activity
      setCategoryRequests(reqRes.data.filter(e => e.action && e.action.includes('category')).slice(0, 10));
      setReportedPosts(reqRes.data.filter(e => e.action && e.action.includes('moderate')).slice(0, 10));
    } catch (err) {
      console.error('Error fetching moderation data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (postId, action) => {
    const reason = action === 'warn' ? window.prompt('Warning message (optional):') : window.prompt('Reason for removal (optional):');
    if (reason === null) return;
    setActionLoading(postId);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`/api/org-admin/moderate?orgId=${orgId}`, { postId, action, reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Post ${action === 'remove' ? 'removed' : 'warned'} successfully.`);
      fetchModerationData();
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <p style={styles.emptyText}>Loading moderation data...</p>;

  return (
    <div>
      <h1 style={styles.pageTitle}>🛡️ Moderation</h1>
      <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px' }}>
        Manage reported content and category requests. You can see post content but <strong>not</strong> who posted it (privacy preserved).
      </p>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ ...styles.metricCard, textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>📝 Category Requests</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
            Employees can request new identity categories. Approve or reject them here.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '24px', fontWeight: '700', color: '#7C3AED' }}>
            {categoryRequests.length}
          </p>
        </div>
        <div style={{ ...styles.metricCard, textAlign: 'left' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>🚩 Reported Content</h3>
          <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
            Review reported posts. Remove inappropriate content or warn users.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>
            {reportedPosts.length}
          </p>
        </div>
      </div>

      {/* Moderation Actions Info */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e5e7eb' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Available Actions</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>✅</span>
            <div>
              <strong style={{ fontSize: '14px' }}>Approve Category Requests</strong>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>When employees request new identity categories, approve them from the Spaces tab.</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>🗑️</span>
            <div>
              <strong style={{ fontSize: '14px' }}>Remove Posts</strong>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>Remove inappropriate content from org communities. The poster's identity remains hidden.</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <strong style={{ fontSize: '14px' }}>Warn Users</strong>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>Send anonymous warnings to users who violate community guidelines.</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>🔒</span>
            <div>
              <strong style={{ fontSize: '14px' }}>Privacy Preserved</strong>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>You can see reported content but never the identity of who posted it.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Audit Log */}
      {(categoryRequests.length > 0 || reportedPosts.length > 0) && (
        <div style={{ marginTop: '24px', backgroundColor: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>Recent Activity</h3>
          {[...categoryRequests, ...reportedPosts].slice(0, 10).map((entry, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px', color: '#555' }}>
              <strong>{entry.action}</strong> — {new Date(entry.created_at).toLocaleString()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BillingTab() {
  return (
    <div>
      <h1 style={styles.pageTitle}>Billing</h1>
      <div style={styles.settingsCard}>
        <p style={styles.emptyText}>
          Billing management is coming soon. Contact support for plan changes.
        </p>
      </div>
    </div>
  );
}

/* ---- Styles ---- */

const styles = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    flexDirection: 'column',
  },
  // Mobile Top Header
  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#4C1D95',
    color: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  mobileHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  mobileHeaderTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#fff',
  },
  hamburgerBtn: {
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: '#fff',
    fontSize: '22px',
    padding: '8px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  // Mobile Overlay
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 200,
  },
  // Mobile Drawer
  mobileDrawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '80vw',
    backgroundColor: '#fff',
    zIndex: 300,
    transition: 'transform 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
  },
  mobileDrawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
  },
  mobileDrawerClose: {
    background: '#f3f4f6',
    border: 'none',
    fontSize: '18px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileDrawerNav: {
    flex: 1,
    padding: '8px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  mobileDrawerItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    textAlign: 'left',
    cursor: 'pointer',
    background: 'transparent',
    color: '#374151',
  },
  // Desktop Sidebar (hidden on mobile via CSS)
  sidebar: {
    width: '240px',
    backgroundColor: '#4C1D95',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  sidebarHeader: {
    padding: '24px 20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  sidebarTitle: {
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
    color: '#fff',
  },
  nav: {
    flex: 1,
    padding: '12px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  navItem: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  sidebarFooter: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#c4b5fd',
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
  },
  // Main content
  main: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    paddingBottom: '80px', // space for bottom bar on mobile
  },
  // Mobile Bottom Tab Bar
  mobileBottomBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '6px 0 env(safe-area-inset-bottom, 8px)',
    zIndex: 100,
    boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
  },
  bottomBarItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '8px',
    minWidth: '56px',
  },
  bottomBarIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  bottomBarLabel: {
    fontSize: '10px',
    fontWeight: '500',
    lineHeight: 1,
  },
  // Shared
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
  },
  errorCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  errorText: {
    fontSize: '15px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  backBtn: {
    padding: '10px 20px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  tabLoading: {
    fontSize: '15px',
    color: '#6b7280',
    padding: '40px 0',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 16px 0',
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  metricCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#7C3AED',
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
  },
  settingsCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  settingsRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '14px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  settingsLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  settingsValue: {
    fontSize: '14px',
    color: '#6b7280',
  },
  domainTag: {
    display: 'inline-block',
    backgroundColor: '#f3f4f6',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    marginLeft: '4px',
  },
  communityList: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  communityRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    borderBottom: '1px solid #f3f4f6',
  },
  communityName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1f2937',
  },
  communityChannel: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '2px',
  },
  toggleBtn: {
    padding: '6px 16px',
    border: 'none',
    borderRadius: '16px',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default OrgAdminDashboard;
